import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import { Field, FieldProps } from 'formik';
import ValueFormatters from '../valueFormatters';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import { EmailFieldValidator } from '../../validator/emailFieldValidator';
import { IErrorMessage } from '../../components/errorMessage/errorMessage.type';
import { ValidatorRule } from '../../validator/validatorRule.constants';
import SuggestionsInput from '../../components/SuggestionsInput';
import { MobileEmailValidator } from './MobileEmailValidator';

interface IMobileEmailAddressFieldProps {
    autoFocus?: boolean;
    intl: InjectedIntl;
    value?: string;
    required?: boolean;
    validatorRules?: ValidatorRule[];
}

interface IMobileEmail {
    name: string;
    domain: string;
}

interface IMobileEmailAddressFieldState {
    mobileItems: IMobileEmail[];
}

class MobileEmailField extends React.Component<
    IMobileEmailAddressFieldProps,
    IMobileEmailAddressFieldState
> {
    private readonly ID: string;
    private readonly LABEL_ID: string;
    private readonly ERROR_REQUIRED_ID: string;
    private emailFieldValidator: EmailFieldValidator;

    constructor(props: IMobileEmailAddressFieldProps) {
        super(props);

        this.ID = `mobileEmail`;
        this.LABEL_ID = 'label.mobile_email';
        this.ERROR_REQUIRED_ID = 'error.mobile_email_address_required';
        this.state = {
            mobileItems: [],
        };

        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);
        const { validatorRules = [ValidatorRule.EmailLengthRule, ValidatorRule.JPEmailRule] } =
            props;

        this.emailFieldValidator = new EmailFieldValidator(validatorRules);
    }

    private validate(value: string): IErrorMessage {
        const { intl, required } = this.props;

        if (!value && required) {
            return intl.formatMessage(
                TranslationHelpers.getMessageDescriptor(this.ERROR_REQUIRED_ID),
            );
        }

        let errorMessage: IErrorMessage = '';
        if (value) {
            const err = this.emailFieldValidator.validate(value, this.LABEL_ID);
            if (err !== null) {
                errorMessage = err.message;
            }
        }

        if (value && !MobileEmailValidator.mobileEmailValidation(value)) {
            return intl.formatMessage(
                TranslationHelpers.getMessageDescriptor('error.mobile_email_address_invalid'),
            );
        }

        return errorMessage;
    }

    public render() {
        return (
            <Field name={this.ID} validate={this.validate} render={this.renderInputField}></Field>
        );
    }

    private renderInputField({
        form: { touched, errors, setFieldValue, handleBlur },
    }: FieldProps): ReactNode {
        const { value, required } = this.props;
        const isTouched = touched && touched[this.ID];
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;
        const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
            setFieldValue(this.ID, ValueFormatters.trimExtraSpaces(event.target.value));
            handleBlur(event);
        };

        const generateSuggestions = (value: string): IMobileEmail[] => {
            const suggestions = MobileEmailValidator.generateSuggestions(value);
            const newSuggestions = suggestions.map((value, _) => {
                return {
                    name: value,
                    domain: value,
                };
            });

            return newSuggestions;
        };

        const onSelectItemClick = (item: IMobileEmail) => {
            if (item) {
                setFieldValue(this.ID, item.domain);
            }
        };

        return (
            <SuggestionsInput<IMobileEmail, keyof IMobileEmail>
                generateSuggestions={generateSuggestions}
                id={this.ID}
                qaId={'mobile-email'}
                searchKey={'name'}
                label={TranslationHelpers.getMessageDescriptor(this.LABEL_ID)}
                error={errorMessage}
                required={required}
                value={value || ''}
                onBlur={onBlur}
                onItemSelected={onSelectItemClick}
                toLowercase={true}
            />
        );
    }
}

export default injectIntl(MobileEmailField);
